import React, { useState, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/Avatar';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup, { type TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Text, Pressable, xcss } from '@atlaskit/primitives';

import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';

import { i18n } from '../i18n.ts';
import type { UserShape } from '../ownership-models.ts';
import {
	withContentOwnershipContextProvider,
	useContentOwnershipContext,
} from '../ContentOwnershipContext.tsx';

import { getUserAvatarSrc } from './getUserAvatarSrc.ts';
import { useDuplicateRef } from './useDuplicateRef.ts';

const LazyPopupContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceViewsMenuContent" */ './PageOwnershipByLineWithContributorsDropdownContent.tsx'
			)
		).PageOwnershipByLineWithContributorsDropdownContent,
});

const triggerStyles = xcss({
	borderRadius: 'radius.small',
	paddingInline: 'space.050',
	paddingBlock: 'space.025',
	backgroundColor: 'color.background.neutral.subtle',
	textOverflow: 'ellipsis',
	whiteSpace: 'normal',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const previewPanelCardStyle = xcss({
	paddingInline: 'space.050',
	paddingBlock: 'space.025',
	textOverflow: 'ellipsis',
	whiteSpace: 'normal',
});

type PageOwnershipByLineWithContributorsDropdownProps = {
	owner: UserShape | null;
	author: UserShape;
	contributorCountWithoutOwner: number;
	analyticsSource: string;
	generatedBy?: string | null;
	onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void;
};
const PageOwnershipByLineWithContributorsDropdownComponent = ({
	owner,
	author,
	contributorCountWithoutOwner,
	analyticsSource,
	generatedBy,
}: PageOwnershipByLineWithContributorsDropdownProps) => {
	const intl = useIntl();
	const { openModal: openChangeOwnerModal } = useContentOwnershipContext();

	const [contentId] = usePageContentId();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [triggerRefDuplicate, setTriggerRefDuplicate] = useState<RefObject<HTMLElement> | null>(
		null,
	);
	const createDuplicateRef = useDuplicateRef();

	// Reset state when changing contentId
	useEffect(() => {
		setIsDropdownOpen(false);
	}, [contentId]);

	const pageOwner = owner ?? author;
	const avatarSrc = getUserAvatarSrc(pageOwner);

	const profileCardResidesInPreviewPanel = isInPreviewPanel();

	const onPopupTriggerClick = useCallback(() => {
		setIsDropdownOpen((prev) => !prev);
	}, []);

	const onPopupClose = useCallback(() => {
		setIsDropdownOpen(false);
	}, []);

	const onChangeOwnerClick = useCallback(() => {
		// accessibility: return focus to dropdown trigger when change owner modal closes - doesnt happen automatically because the change owner modal trigger is in the dropdown which gets unmounted when the modal opens.
		openChangeOwnerModal({ returnFocusRef: triggerRefDuplicate ?? undefined });
		setIsDropdownOpen(false);
	}, [openChangeOwnerModal, triggerRefDuplicate]);

	const ownerInfoInsidePreviewPanel = (
		<Inline space="space.050" alignBlock="center" xcss={previewPanelCardStyle}>
			<Avatar src={avatarSrc} borderColor="transparent" size="xsmall" />
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{intl.formatMessage(
					generatedBy === 'ONBOARDING'
						? i18n.bylineMainTextCreatedByConfluence
						: i18n.abbreviatedBylineMainText,
					{ user: pageOwner.fullName },
				)}
			</Text>
		</Inline>
	);

	const popupTrigger = ({ ref, ...triggerProps }: TriggerProps) => {
		const triggerRef = createDuplicateRef(ref, setTriggerRefDuplicate);
		return (
			<Tooltip
				content={
					isDropdownOpen ? undefined : intl.formatMessage(i18n.ownerWithContributorsDropdownTooltip)
				}
				position="bottom"
				hideTooltipOnClick
			>
				<Pressable
					testId="owner-with-contributors-dropdown-trigger"
					xcss={triggerStyles}
					onClick={onPopupTriggerClick}
					ref={triggerRef}
					{...triggerProps}
				>
					<Inline space="space.050" alignBlock="center">
						<Avatar src={avatarSrc} borderColor="transparent" size="xsmall" />
						<Text size="small" color="color.text.subtle" maxLines={1}>
							{intl.formatMessage(
								generatedBy === 'ONBOARDING'
									? i18n.bylineMainTextCreatedByConfluence
									: i18n.abbreviatedBylineMainText,
								{ user: pageOwner.fullName },
							)}
						</Text>
					</Inline>
				</Pressable>
			</Tooltip>
		);
	};

	if (profileCardResidesInPreviewPanel) {
		return ownerInfoInsidePreviewPanel;
	}
	return (
		<Popup
			content={() => (
				<LazyPopupContent
					analyticsSource={`${analyticsSource}-byline`}
					owner={pageOwner}
					onChangeOwnerClick={onChangeOwnerClick}
					contributorCountWithoutOwner={contributorCountWithoutOwner}
				/>
			)}
			trigger={popupTrigger}
			isOpen={isDropdownOpen}
			onClose={onPopupClose}
			placement="bottom-start"
			shouldRenderToParent={expValEquals('a11y-fixes-week2-may-2026', 'isEnabled', true)}
		/>
	);
};

export const PageOwnershipByLineWithContributorsDropdown = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXPERIENCES,
})(withContentOwnershipContextProvider(PageOwnershipByLineWithContributorsDropdownComponent));
